/**
 * ForgotPasswordPage — email input → POST /auth/forgot-password.
 *
 * Backend always returns 200 + generic ack regardless of whether the
 * email is registered, so this page can show the same "if an account
 * exists we sent a link" message in both cases (the user can't tell —
 * by design, no enumeration).
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, ArrowLeft } from "lucide-react";
import { authService } from "@/services";

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  usePageTitle("Forgot password");
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await authService.forgotPassword(email);
    } catch {
      // Even on a network error we show the same "check your email" message.
      // Real users won't notice; an attacker probing for valid emails gets
      // the same response in both cases.
    }
    setSubmitted(true);
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#E3F2FD] dark:bg-[#0a1428] flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8">
        <Link
          to="/login"
          className="inline-flex items-center text-xs text-[#1A237E]/60 dark:text-[#E3F2FD]/60 hover:text-[#1A237E] dark:text-[#E3F2FD] mb-4"
        >
          <ArrowLeft className="w-3 h-3 mr-1" /> {t("forgotPassword.backToSignIn")}
        </Link>
        <h1 className="text-2xl font-bold text-[#1A237E] dark:text-[#E3F2FD]" style={{ fontFamily: "Fredoka" }}>
          {t("forgotPassword.title")}
        </h1>
        {submitted ? (
          <p
            data-testid="forgot-success-msg"
            className="mt-4 text-sm text-[#1A237E]/80 dark:text-[#E3F2FD]/80"
          >
            {t("forgotPassword.successMessage", { email })}
          </p>
        ) : (
          <>
            <p className="mt-2 text-sm text-[#1A237E]/70 dark:text-[#E3F2FD]/70">
              {t("forgotPassword.instructions")}
            </p>
            <form onSubmit={handleSubmit} className="mt-6 space-y-3">
              <div className="relative">
                <Mail className="absolute left-3 top-3 w-4 h-4 text-[#1A237E]/30 dark:text-[#E3F2FD]/30" />
                <Input
                  data-testid="forgot-email"
                  type="email"
                  placeholder={t("forgotPassword.field.email")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-white/60 dark:bg-slate-900/60 border-[#1A237E]/10 dark:border-[#E3F2FD]/10 rounded-xl h-11"
                  required
                />
              </div>
              <Button
                data-testid="forgot-submit-btn"
                type="submit"
                disabled={loading || !email}
                className="w-full h-12 rounded-full bg-[#38B6FF] hover:bg-[#1A237E] text-white font-bold"
              >
                {loading ? t("forgotPassword.button.sending") : t("forgotPassword.button.send")}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
